import { useCallback, useEffect, useState } from 'react';
import {
	checkLifetimeAccess,
	checkSubscriptionAccess,
	getSubscriptionInterval,
} from '../services/stripe.service';
import { ACCESS_TYPES, INTERVALS } from '../config/constants';

// Checks whether the current user has access (lifetime or subscription).
// recheckAccess can be called after a purchase to re-evaluate without a page reload.
// Returns hasAccess (boolean), accessType (ACCESS_TYPES value), loading, and recheckAccess.
export const useAccess = (user) => {
	const [hasAccess, setHasAccess] = useState(null);
	const [accessType, setAccessType] = useState(null);
	const [loading, setLoading] = useState(true);

	const recheckAccess = useCallback(async () => {
		if (!user) {
			setHasAccess(null);
			setAccessType(null);
			setLoading(false);
			return;
		}

		setLoading(true);
		try {
			const hasLifetime = await checkLifetimeAccess(user.uid);
			if (hasLifetime) {
				setHasAccess(true);
				setAccessType(ACCESS_TYPES.LIFETIME);
				return;
			}

			const hasSubscription = await checkSubscriptionAccess(user);
			setHasAccess(hasSubscription);
			if (hasSubscription) {
				const interval = await getSubscriptionInterval(user.uid);
				setAccessType(
					interval === INTERVALS.YEAR
						? ACCESS_TYPES.SUBSCRIPTION_YEARLY
						: ACCESS_TYPES.SUBSCRIPTION_MONTHLY,
				);
			} else {
				setAccessType(ACCESS_TYPES.NONE);
			}
		} finally {
			setLoading(false);
		}
	}, [user]);

	useEffect(() => {
		recheckAccess();
	}, [recheckAccess]);

	return { hasAccess, accessType, loading, recheckAccess };
};
